import {
  afterNextRender,
  ChangeDetectorRef,
  Component,
  inject,
  Inject,
  makeStateKey,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  RESPONSE_INIT,
  TransferState,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { EnvToken, CountryToken, SeoSegmentsToken } from '@app/core/env.tokens';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { HowItWorksDialogComponent } from '@app/dialogs/howItWorksDialog/howItWorksDialog';
import { takeUntil } from 'rxjs/operators';
import { DecimalPipe, isPlatformBrowser } from '@angular/common';
import { SEOService } from '@services/logic/seo.service';
// Services
import { CityService } from '@services/http/city.service';
import { RegionStore } from '@services/logic/region-store';
import { TaskService } from '@services/http/task.service';
import { VerticalService } from '@app/services/http/verticals.service';

// Models
import { City } from '@models/Location/city.model';
import { Region } from '@models/Location/region.model';
import { Task } from '@models/task.model';
import { Vertical } from '@models/vertical.model';
import { AllZipCodesRegion } from '@models/Location/allZipCodesRegion.model';

import { urlDecode, isInvalidLocationParam } from '@utils/urlHelpers';
import { minorToMajor } from '@utils/minor-unit';
import { joinSeoPath } from '@utils/joinSeoPath';
import { resolveDisplayPriceBand } from '@utils/resolveDisplayPrice';
import {
  formatTitlePriceToken,
  formatDescriptionPriceSentence,
  composeTitleWithPrice,
} from '@utils/displayPriceToken';

import { JsonLdPipe } from '@app/pipes/json-ld-pipe';
import { TranslatePipe } from '@app/pipes/translate.pipe';

// Components
import { AssociatedTasksComponent } from '@components/seo/associated-tasks/associated-tasks.component';
import { BestAssociatedUsersComponent } from '@components/seoPageElements/best-associated-users/best-associated-users.component';
import { HeroSectionComponent } from '@components/seo/hero-section/hero-section.component';
import { HowItWorksComponent } from '@components/seoPageElements/howItWorks/howItWorks.component';
import { GoodToKnowComponent } from '@components/seoPageElements/good-to-know/good-to-know.component';
import { LinksContainerComponent } from '@components/links-container/links-container.component';
import { FooterComponent } from '@components/shared/footer/footer.component';
import { PriceAndReviewComponent } from '@components/seoPageElements/price-and-review/price-and-review.component';
import { HhSeoDescComponent } from '@components/seo/hhSeoDesc/hh-seo-desc.component';
import { IncludeContentComponent } from '@components/seo/include-content/include-content.component';
import { InsuranceUspComponent } from '@components/seoPageElements/insuranceUsp/insuranceUsp.component';
import { FaqContentComponent } from '@components/seoPageElements/faq-content/faq-content.component';
import { RelatedServicesComponent } from '@components/seoPageElements/related-services/related-services.component';
import { ValueLineComponent } from '@components/seoPageElements/valueLine/valueLine.component';
import { WhyHandyhandComponent } from '@components/seoPageElements/whyPinployComponent/whyHandyhandComponent';
import { WhyContentComponent } from '@components/seoPageElements/why-content/why-content.component';
import { ExternalService } from '@app/services/http/external.service';
import { NavigationHistoryService } from '@app/services/logic/navigation-history.service';
import {
  catchError,
  map,
  merge,
  Observable,
  of,
  shareReplay,
  Subject,
} from 'rxjs';
import { ServerSideResponseService } from '@app/services/logic/serverSideResponse.service';
import { HappyHelperBannerComponent } from '@components/seoPageElements/happy-helper-banner/happy-helper-banner.component';
import { TrustpilotWidgetComponent } from '@components/seoPageElements/trustpilot-widget/trustpilot-widget.component';
import { BreadcrumbListComponent } from '@components/shared/breadcrumb-list/breadcrumb-list.component';
import { setBreadCrumb } from '@utils/setBreadCrumb';
import {
  DeepLinkButton,
  DeepLinkButtonsComponent,
} from '@components/shared/deep-link-buttons/deep-link-buttons.component';
import { getSeoCountryConfig } from './country-config';
import { HttpErrorResponse } from '@angular/common/http';

// TransferState keys for SSR data consistency - will be made unique per route
const HERO_IMAGE_KEY_PREFIX = 'hero-image-url';

// Outcome of the city/region zip-code lookup, fetched in parallel with the
// vertical itself since it only depends on route params (region/city slug),
// never on the vertical response.
type CityFetchOutcome =
  | { ok: true; result: AllZipCodesRegion | null }
  | { ok: false; err: HttpErrorResponse }
  | null;

@Component({
  imports: [
    LinksContainerComponent,
    FooterComponent,
    JsonLdPipe,
    HeroSectionComponent,
    ValueLineComponent,
    BestAssociatedUsersComponent,
    HowItWorksComponent,
    PriceAndReviewComponent,
    AssociatedTasksComponent,
    HhSeoDescComponent,
    IncludeContentComponent,
    WhyHandyhandComponent,
    InsuranceUspComponent,
    FaqContentComponent,
    GoodToKnowComponent,
    WhyContentComponent,
    RelatedServicesComponent,
    HappyHelperBannerComponent,
    TrustpilotWidgetComponent,
    BreadcrumbListComponent,
    DeepLinkButtonsComponent,
  ],
  providers: [
    DecimalPipe,
    SEOService,
    TranslatePipe,
    ExternalService,
    NavigationHistoryService,
  ],
  selector: 'app-seo',
  templateUrl: './seo.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./seo.component.scss'],
})
export class SeoComponent implements OnInit, OnDestroy {
  protected readonly env = inject(EnvToken);
  protected readonly seg = inject(SeoSegmentsToken);
  private readonly country = inject(CountryToken);
  private readonly seoCountryConfig = getSeoCountryConfig(this.country);
  private readonly dialog = inject(MatDialog);

  public isBrowser: boolean;
  private readonly responseInit = inject(RESPONSE_INIT, { optional: true });
  @ViewChild(TrustpilotWidgetComponent, { static: false })
  trustpilotWidget?: TrustpilotWidgetComponent;
  private destroy$ = new Subject<void>();
  private cancel$ = new Subject<void>();
  relTasks: Task[] = [];
  vertical: Vertical | null = null;
  city?: City;
  region?: Region;
  canonicalUrl?: string;
  category: any;
  partsOfUrl!: { name: string; url: string }[];

  // URL Params
  private headCategory!: string;
  private subCategory?: string;
  private subSubCategory?: string;

  hhJobIntroTitle?: string;
  hhJobIntroContent?: string;
  hhJobEarnTitle?: string;
  locationName?: string;
  currentParams?: string;
  priceReviewShowing?: boolean;

  getQuoteChosen?: string;
  content: any;
  q25Price: number = 0;
  q50Price: number = 0;
  q75Price: number = 0;
  /** True when the prices above came from `vertical.displayPrice`, not live offers. */
  priceIsFallback = false;

  schema: any;
  iOrPa?: string;
  cityOrRegionText = '';
  url?: string;

  // If url contains a city
  isCityUrl = false;
  isRegionUrl = false;
  private routeCitySlug?: string;
  // Structural up-link to the national vertical page (city/region pages only)
  nationalUrl: string | null = null;
  nationalLinkLabel = '';
  nationalLinkPageType: 'city' | 'region' | null = null;
  isFindPage = false;
  isJobsPage = false;
  noRelTasks = false;
  priceAndReviewRendered = false;
  hasAssociatedTasks = false;
  hasFaqContent = false;
  hasGoodToKnow = false;
  verticalH1?: string;

  // Deep link buttons
  deepLinkButtons: DeepLinkButton[] = [];
  private isHowItWorksDialogOpen = false;
  hasWhatItIncludesArea = false;
  jobEarnAsContent?: string;
  goodToKnowContent?: string;
  relatedServicesContent?: any;
  hasVertical = false;
  public heroImgUrl: string = '';
  /** The served hero image's own altText — hero-section's fallback when the vertical has no `imgAltAttribute`. */
  public heroImgAlt: string = '';
  public isNetherland: boolean = this.country === 'nl';
  public innerWidth: number = 1200; // Default value for SSR compatibility
  private currentCacheKeys: Set<string> = new Set(); // Track current cache keys
  private isLoadingData = false; // Prevent multiple data loading
  private hasProcessedData = false; // Prevent multiple data processing
  private hydrationTimeout?: number; // Track hydration timeout

  constructor(
    private activatedRoute: ActivatedRoute,
    private cityService: CityService,
    public externalService: ExternalService,
    @Inject(PLATFORM_ID) private platformId: Object,
    private router: Router,
    private seoService: SEOService,
    private regionStore: RegionStore,
    private taskService: TaskService,
    private verticalNoCityService: VerticalService,
    private t: TranslatePipe,
    private transferState: TransferState,
    private cdr: ChangeDetectorRef,
    private serverSideResponseService: ServerSideResponseService,
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);

    // Update browser-specific values after hydration
    afterNextRender(() => {
      this.innerWidth = window.innerWidth;
      this.cdr.markForCheck();
    });
  }

  ngOnInit(): void {
    this.url = this.router.url.split('/')[1];

    // Check for duplicate parameters and redirect if found
    this.checkAndRedirectForDuplicates();

    // Set isFindPage/isJobsPage from route data synchronously first
    this.isFindPage = this.activatedRoute.snapshot.data['isFindPage'];
    this.isJobsPage = !this.isFindPage;
    // Also subscribe to updates for dynamic route changes
    this.activatedRoute.data
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        this.isFindPage = data['isFindPage'];
        this.isJobsPage = !this.isFindPage;
        this.cdr.markForCheck();
      });

    this.activatedRoute.paramMap
      .pipe(takeUntil(this.destroy$))
      .subscribe((params) => {
        // Cancel any in-flight requests from previous route
        this.cancel$.next();

        // Clear old cache entries when route parameters change
        this.clearOldCacheEntries();

        // Reset loading flags when route changes
        this.isLoadingData = false;
        this.hasProcessedData = false;

        // Reset component state so child components unmount and cancel their
        // own HTTP requests (review-count-tag, fetchAssociatedTasks, etc.).
        // They will only re-mount once fetchVertical succeeds and hasVertical
        // is set to true again.
        this.hasVertical = false;
        this.hasAssociatedTasks = false;
        this.hasFaqContent = false;
        this.hasGoodToKnow = false;
        this.priceAndReviewRendered = false;
        this.resetPriceState();

        this.headCategory = params.get('headCategory') ?? '';
        this.subCategory = params.get('subCategory') ?? '';
        this.subSubCategory = params.get('subSubCategory') ?? '';
        const region = params.get('region');
        const urlCityParam = params.get('city');

        // Serialization artifacts must never render as pages (SEO: 404, not 200)
        if (
          isInvalidLocationParam(region) ||
          isInvalidLocationParam(urlCityParam)
        ) {
          this.serverSideResponseService.setNotFound();
          if (this.responseInit) {
            this.responseInit.status = 404;
          }
          this.navigateTo('404');
          this.cdr.markForCheck();
          return;
        }

        this.region = this.getRegion(region);
        this.routeCitySlug =
          urlDecode(urlCityParam ?? '')?.toLowerCase() || undefined;

        const currentSlug = urlDecode(
          this.subSubCategory || this.subCategory || this.headCategory,
        )?.toLowerCase();

        this.fetchVertical(
          urlDecode(
            this.subSubCategory || this.subCategory || this.headCategory,
          ).toLowerCase(),
          this.region?.regionURL || region,
          urlDecode(urlCityParam ?? '')?.toLowerCase(),
        );
        this.cdr.markForCheck();
      });
  }

  /**
   * Check for duplicate parameters in URL and redirect if found
   */
  private checkAndRedirectForDuplicates(): void {
    if (!this.isBrowser) return;

    const currentUrl = this.router.url;
    const cleanedUrl = this.removeDuplicateParams(currentUrl);

    // If the cleaned URL is different from current URL, redirect
    if (cleanedUrl !== currentUrl) {
      const fullCleanedUrl = `${this.env.domain}${cleanedUrl}`;
      // Use 303 redirect (See Other) for SEO-friendly redirect
      window.location.replace(fullCleanedUrl);
    }
  }

  /**
   * Generate unique cache key for TransferState based on route parameters
   */
  private generateCacheKey(
    prefix: string,
    slug: string,
    region?: string,
    city?: string,
  ): string {
    const keyParts = [prefix, slug];
    if (region) keyParts.push(region);
    if (city) keyParts.push(city);
    return keyParts.join('-');
  }

  /**
   * Clear old TransferState cache entries to prevent memory leaks
   * This should be called when navigating to a new route
   */
  private clearOldCacheEntries(): void {
    if (this.isBrowser) {
      // Clear previously tracked cache keys
      for (const key of this.currentCacheKeys) {
        const stateKey = makeStateKey(key);
        if (this.transferState.hasKey(stateKey)) {
          this.transferState.remove(stateKey);
        }
      }
      this.currentCacheKeys.clear();
    }
  }

  private fetchHeroImageWithTransferState(slug: string): void {
    // A failed or empty lookup must not leave the previous route's hero
    // showing after client-side navigation — clear before fetching.
    this.heroImgUrl = '';
    this.heroImgAlt = '';
    const cacheKey = this.generateCacheKey(
      HERO_IMAGE_KEY_PREFIX,
      slug,
      this.region?.regionURL,
      this.city?.slug,
    );
    const heroImageKey = makeStateKey<
      { url: string; altText?: string } | string
    >(cacheKey);

    // Track this cache key
    this.currentCacheKeys.add(cacheKey);

    // Check if data exists in TransferState (client-side)
    if (this.isBrowser && this.transferState.hasKey(heroImageKey)) {
      const hero = this.transferState.get(heroImageKey, { url: '' });
      // Pre-altText SSR bundles stored the bare URL string — accept both
      // shapes so a mixed-version deploy window doesn't blank the hero.
      const url = typeof hero === 'string' ? hero : (hero?.url ?? '');
      if (url) {
        this.heroImgUrl = url;
        this.heroImgAlt = typeof hero === 'string' ? '' : (hero?.altText ?? '');
        return;
      }
      // Empty or invalid payload: fall through and fetch live.
    }

    this.verticalNoCityService
      .fetchVerticalHeroImage(slug)
      .pipe(
        map((res) =>
          res?.url ? { url: res.url, altText: res.altText } : undefined,
        ),
        catchError((error) => {
          console.warn('Error fetching hero image:', error);
          return of(undefined);
        }),
        takeUntil(merge(this.destroy$, this.cancel$)),
      )
      .subscribe({
        next: (hero) => {
          if (hero) {
            this.heroImgUrl = hero.url;
            this.heroImgAlt = hero.altText ?? '';
            if (!this.isBrowser) {
              this.transferState.set(heroImageKey, hero);
            }
            this.cdr.markForCheck();
          }
        },
        error: (error) => {
          console.warn('Error in hero image subscription:', error);
        },
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.cancel$.next();
    this.cancel$.complete();

    // Clear any pending hydration timeout
    if (this.hydrationTimeout) {
      clearTimeout(this.hydrationTimeout);
    }
  }

  private navigateTo(url: string) {
    // skipLocationChange keeps the requested URL during SSR so the engine
    // renders the error page with the 404 status instead of attempting an
    // HTTP redirect (redirects must be 3xx and would throw / 500).
    return this.router.navigate([`/${url}`], { skipLocationChange: true });
  }

  /**
   * Kick off the city/region zip-code lookup and make it hot immediately
   * (eager `subscribe` + `shareReplay`) so it fires in parallel with
   * fetchVertical's HTTP call instead of only starting once the vertical
   * response comes back. The two requests are independent — this call only
   * needs the region/city slug from route params — so chaining them was
   * adding a full extra network round-trip to every city/region page's
   * critical path, which is what pushed SSR renders past the 4s budget.
   */
  private fetchCityData(): Observable<CityFetchOutcome> {
    if (!this.city && !this.region) {
      return of(null);
    }

    const city$: Observable<CityFetchOutcome> = this.cityService
      .findByRegionAndCity(this.region?.regionURL ?? '', this.city?.slug ?? '')
      .pipe(
        map((result) => ({ ok: true as const, result })),
        catchError((err: HttpErrorResponse) => of({ ok: false as const, err })),
        takeUntil(merge(this.destroy$, this.cancel$)),
        shareReplay(1),
      );

    // Subscribe now to fire the HTTP request eagerly; later subscribers
    // (processVerticalData) replay the cached result instead of re-fetching.
    city$.subscribe();

    return city$;
  }

  /**
   * Process vertical data after fetching or retrieving from TransferState
   */
  private processVerticalData(
    vertical: Vertical,
    city$: Observable<CityFetchOutcome>,
  ): void {
    if (this.hasProcessedData) {
      return;
    }

    this.hasProcessedData = true;

    // Fetch hero image only after vertical succeeds (avoids wasted calls on 404s)
    const slug = vertical.newSlug || vertical.slug;
    if (slug) {
      this.fetchHeroImageWithTransferState(slug.toLowerCase());
    }

    const verticalCityResult: { region?: Region; zipCodes: number[] } = {
      region: this.region,
      zipCodes: [],
    };

    city$.pipe(takeUntil(merge(this.destroy$, this.cancel$))).subscribe({
      next: (outcome) => {
        if (!outcome) {
          this.finalizeVerticalData(vertical, verticalCityResult);
          return;
        }
        if (outcome.ok) {
          verticalCityResult.zipCodes = outcome.result?.zipCodes ?? [];
          if (this.region) {
            this.isRegionUrl = true;
          }
          this.isCityUrl = true;
          this.finalizeVerticalData(vertical, verticalCityResult);
          return;
        }
        if (outcome.err?.status === 404) {
          if (this.responseInit) {
            this.responseInit.status = 404;
          }
          this.navigateTo('404');
          return;
        }
        this.finalizeVerticalData(vertical, verticalCityResult);
      },
    });
  }

  private finalizeVerticalData(
    vertical: Vertical,
    verticalCityResult: any,
  ): void {
    this.vertical = {
      ...vertical,
      ...verticalCityResult,
    };

    this.hasVertical = true;

    if (!this.vertical) {
      return;
    }

    if (
      this.vertical.similiarTasks?.length > 2 &&
      (!this.region || !this.isCityUrl)
    ) {
      this.relTasks = this.vertical?.similiarTasks;
      this.hasAssociatedTasks = this.relTasks.length > 0;
      this.setAssociatedTasks(this.relTasks);
    } else if (this.vertical.taskExamples?.length > 2) {
      this.relTasks = this.vertical?.taskExamples;
      this.hasAssociatedTasks = this.relTasks.length > 0;
      this.setAssociatedTasks(this.relTasks);
    } else {
      this.fetchAssociatedTasks(vertical?.tagId);
    }

    // Runs on every path, including the one that returns early during SSR:
    // `fetchAssociatedTasks` is browser-only, so without this a vertical with
    // fewer than three stored tasks renders no price at all server-side.
    this.applyDisplayPriceFallback();

    this.hhJobIntroTitle = this.vertical.content?.job_intro_title
      ? this.vertical.content?.job_intro_title
      : undefined;

    this.hhJobIntroContent = this.vertical.content?.job_intro_content
      ? this.vertical.content?.job_intro_content
      : undefined;

    this.hhJobEarnTitle = this.vertical.content?.job_earn_as_title
      ? this.vertical.content?.job_earn_as_title
      : undefined;
    this.setCityOrRegionText();
    this.getQuoteChosen = this.vertical.s1;
    this.hasFaqContent = !!this.vertical?.content?.FAQ_area;
    this.hasGoodToKnow = !!this.vertical?.content?.GOOD_TO_KNOW_area;
    this.verticalH1 = this.vertical?.h1;
    this.setupDeepLinkButtons();
    this.hasWhatItIncludesArea =
      !!this.vertical?.content?.WHAT_IT_INCLUDES_area;
    this.jobEarnAsContent = this.vertical?.content?.job_earn_as_content;
    this.goodToKnowContent = this.vertical?.content?.GOOD_TO_KNOW_area;
    this.relatedServicesContent = this.vertical?.content?.RELATED_SERVICES_area;
    this.setNationalLink();
    this.setMetaData();

    // Breadcrumbs must be set before injectJSONLDSchema — the
    // BreadcrumbList schema is built from partsOfUrl
    if (this.isJobsPage) {
      this.setBreadCrumbJobPage(this.vertical);
    } else {
      this.setBreadCrumb(this.vertical);
    }
    this.injectJSONLDSchema();

    // Re-initialize Trustpilot widget after data is loaded
    this.reinitializeTrustpilotWidget();

    this.cdr.markForCheck();
  }

  /**
   * Structural up-link from city (/b) and region (/r) vertical pages to the
   * national /hh page. Never set on national or jobs pages, so no self-links
   * render. The path mirrors the canonical national URL: the vertical's
   * category prefix + slug + the service-vertical suffix.
   */
  private setNationalLink(): void {
    this.nationalUrl = null;
    this.nationalLinkLabel = '';
    this.nationalLinkPageType = null;

    const isGeoPage = !!(this.routeCitySlug || this.region);
    if (!this.isFindPage || !isGeoPage || !this.vertical) {
      return;
    }

    const slug = this.vertical.newSlug?.trim() || this.vertical.slug;
    this.nationalUrl = joinSeoPath([
      ...(this.vertical.mainCategory?.slug
        ? [this.vertical.mainCategory.slug]
        : []),
      ...(this.vertical.subCategory?.slug
        ? [this.vertical.subCategory.slug]
        : []),
      slug,
      this.seg.suffix.serviceVertical,
    ]);
    if (!this.nationalUrl) {
      return;
    }

    this.nationalLinkPageType = this.routeCitySlug ? 'city' : 'region';
    const labelTemplate = this.t.transform('COMPONENTS.HERO.NATIONAL_LINK');
    this.nationalLinkLabel = labelTemplate.replace(
      /\{\{service\}\}/g,
      this.capitalizeFirstLetter(this.vertical.cat ?? ''),
    );
  }

  private getRegion(region: string | null): Region | undefined {
    if (!region) return undefined;
    const decodedRegion = urlDecode(region.toLowerCase().trim());
    return this.regionStore.findActiveByURL(decodedRegion);
  }

  isCleaningService(): boolean {
    if (!this.vertical) return false;
    const slug = this.vertical?.newSlug || this.vertical.slug;
    return this.seoCountryConfig.cleaningSlugs.includes(
      slug?.toLowerCase() ?? '',
    );
  }

  /**
   * Fetch vertical data with TransferState support for SSR consistency
   */
  private fetchVertical(
    slug: string,
    regionName?: string | null,
    citySlug?: string | undefined,
  ) {
    // Prevent multiple data loading
    if (this.isLoadingData) {
      return;
    }

    this.isLoadingData = true;

    // Fire the city/region lookup now, in parallel with fetchVertical below —
    // see fetchCityData() for why this no longer waits on the vertical response.
    const city$ = this.fetchCityData();

    // No manual TransferState here: the built-in HTTP transfer cache
    // (withHttpTransferCacheOptions in app.config.ts) already carries this
    // response to the client, so the post-hydration re-fetch is served from
    // ng-state without a network call. A manual copy duplicated the ~77KB
    // vertical payload in every page's ng-state.
    this.verticalNoCityService
      .fetchVerticalById(
        slug,
        regionName || this.region?.regionURL,
        citySlug,
        this.headCategory,
        this.subCategory,
        this.subSubCategory,
        // isJobPage must be true ONLY for /jobs pages: the dashboard skips
        // category-mismatch 301 canonicalization when isJobPage is set.
        !this.isFindPage,
      )
      .pipe(takeUntil(merge(this.destroy$, this.cancel$)))
      .subscribe(
        (vertical: Vertical): void => {
          this.processVerticalData(vertical, city$);
          this.isLoadingData = false;
        },
        (err) => {
          this.isLoadingData = false;
          if (err.status === 301 && err.error?.redirectTo) {
            const redirectTo: string = err.error.redirectTo;
            if (this.isBrowser) {
              this.router.navigateByUrl(redirectTo, { replaceUrl: true });
            } else if (this.responseInit) {
              this.responseInit.status = 301;
              this.responseInit.headers = { Location: redirectTo };
            }
            this.cancel$.next();
            return;
          }

          if (err.status === 404) {
            // Set 404 status code for server response during SSR
            this.serverSideResponseService.setNotFound();
            // Cancel all in-flight requests (hero image, etc.)
            this.cancel$.next();
            if (this.responseInit) {
              this.responseInit.status = 404;
            }
            return this.navigateTo('404');
          }

          // For transient errors (ECONNRESET, timeout, 5xx), mark as no data
          // so the page still renders rather than hanging indefinitely.
          this.hasVertical = false;
          this.noRelTasks = true;
          this.hasAssociatedTasks = false;

          // Ensure the application can still become stable even with errors
          this.cdr.markForCheck();

          return err;
        },
      );
  }

  /**
   * Re-initialize Trustpilot widget
   */
  private reinitializeTrustpilotWidget(): void {
    if (this.isBrowser) {
      // Use requestAnimationFrame instead of setTimeout for better hydration compatibility
      requestAnimationFrame(() => {
        // Re-initialize regular Trustpilot widget
        if (this.trustpilotWidget) {
          this.trustpilotWidget.reinitialize();
        }
      });
    }
  }

  /**
   * Danish grammar word for the CITY on city pages: the server-provided
   * editorial preposition ('på Nørrebro') wins; the legacy config lookup in
   * setIorPa (keyed on official names, which editorial displayNames no longer
   * match) remains as the fallback.
   */
  private cityPreposition(): string {
    return (
      this.vertical?.city?.preposition ||
      this.iOrPa ||
      this.seoCountryConfig.prepositions.default
    );
  }

  private setIorPa(cityOrRegion: string): void {
    const {
      exceptionCities,
      exceptionRegions,
      exception,
      default: defaultPrep,
    } = this.seoCountryConfig.prepositions;
    const isMatchedCity = exceptionCities.some((c) =>
      cityOrRegion?.startsWith(c),
    );
    const isMatchedRegion = exceptionRegions.includes(cityOrRegion);
    if (isMatchedCity || isMatchedRegion) {
      this.iOrPa = exception;
    } else {
      this.iOrPa = defaultPrep;
    }
  }

  private getCitySlug(): string | undefined {
    return this.vertical?.city?.slug || undefined;
  }

  private getCityDisplayName(): string | undefined {
    if (this.vertical?.city?.city) {
      return this.capitalizeWords(
        this.cityTransformations(this.vertical.city.city),
      );
    }
    return undefined;
  }

  private setCityOrRegionText() {
    if (!this.vertical) {
      return;
    }

    const cityName = this.getCityDisplayName();
    if (this.routeCitySlug && cityName) {
      this.setIorPa(this.vertical?.city?.city ?? cityName);
      this.locationName = `${this.cityPreposition()} ${cityName}`;
    } else if (this.vertical.region) {
      this.setIorPa(this.vertical.region.name);
      this.locationName = `${this.iOrPa} ${this.capitalizeWords(
        this.regionTransformations(this.vertical.region.name),
      )}`;
    }
    this.cityOrRegionText = ` ${this.locationName ? this.locationName : ''}`;
  }

  capitalizeWords(str: string) {
    if (!str) {
      return '';
    }
    const extra = this.seoCountryConfig.locale.extraLowerCaseChars;
    const pattern = new RegExp(`(^|[ \\t])([a-z${extra}])`, 'g');
    return str.replace(
      pattern,
      (_, prefix, match) => prefix + match.toUpperCase(),
    );
  }

  ensureNoSpacesBeforePeriod(str: string): string {
    return str.replace(/\s+\./g, '.');
  }

  private cityTransformations(city: string): string {
    return this.seoCountryConfig.cityTransformations[city] || city;
  }

  private regionTransformations(region: string): string {
    return this.seoCountryConfig.regionTransformations[region] || region;
  }

  /**
   * Removes duplicate consecutive parameters from URL path
   * Example: /rengoering/rengoering/hh becomes /rengoering/hh
   * @param urlPath - The URL path to clean
   * @returns Cleaned URL path with duplicates removed
   */
  private removeDuplicateParams(urlPath: string): string {
    if (!urlPath) return urlPath;

    const segments = urlPath.split('/').filter((segment) => segment.length > 0);
    const cleanedSegments: string[] = [];

    for (let i = 0; i < segments.length; i++) {
      const currentSegment = segments[i];
      const nextSegment = segments[i + 1];

      // Only add current segment if it's not the same as the next one
      if (currentSegment !== nextSegment) {
        cleanedSegments.push(currentSegment);
      }
    }

    return '/' + cleanedSegments.join('/');
  }

  /**
   * Generates a meta title based on the region and city information present in the vertical object.
   *
   * The function follows these rules:
   * 1. If both a region and a city are provided, the meta title is created in the format: " [City Name] | de bedste [sPM] [iOrPa] [City Name]".
   * 2. If only a region is provided, the meta title is in the format: " [iOrPa] [Region Name] | de bedste [sPM] [iOrPa] [Region Name]".
   * 3. If neither a region nor a city is provided, an empty string is returned.
   *
   * @returns {string} The generated meta title or an empty string if neither region nor city are provided.
   */
  private getRegionAndCityMetaTitle(): string {
    if (!this.vertical) {
      return '';
    }

    const cityName = this.getCityDisplayName();
    if (this.routeCitySlug && cityName) {
      return ` ${this.cityPreposition()} ${cityName}`;
    }
    // If only region exists in the vertical object
    if (this.vertical.region) {
      return ` ${this.iOrPa} ${this.capitalizeWords(
        this.regionTransformations(this.vertical.region.name),
      )}`;
    }
    // If neither region nor city exist
    return '';
  }

  private setMetaData() {
    const defaultTitleSuffix = ``;
    const jobTitleSuffix = `| ${this.t.transform('META.EARN')} ${
      this.env.appName
    }`;

    // Helper function to create title for the regular page. Returns the brand
    // decision alongside the text, because a price token may only fit if the
    // ` - ${appName}` suffix gives up its 12 characters.
    const constructRegularTitle = (): {
      title: string;
      appendSiteName: boolean;
    } => {
      let prefix: string = '';

      if (
        this.routeCitySlug ||
        this.vertical?.city?.city ||
        this.vertical?.region
      ) {
        prefix = this.vertical?.titleTagWithGeo ?? '';
      } else {
        prefix = this.vertical?.tT ?? '';
      }
      const location = this.getRegionAndCityMetaTitle();
      const base = `${prefix}${location}${defaultTitleSuffix}`;

      // Render-time price token from `displayPrice` — never stored in
      // `tT`/`titleTagWithGeo`, so a price recompute needs no title rewrite
      // and the price lands after the location. Skipped on an empty stem: a
      // title must never *start* with the price.
      if (!prefix) {
        return { title: base, appendSiteName: true };
      }
      const priceToken = formatTitlePriceToken(
        this.vertical?.displayPrice,
        this.seoCountryConfig.priceToken,
      );
      // The SeoService appends ` - ${siteName}` unless appendSiteName is false,
      // so the 60-char SERP budget has to hold for the composed result. When
      // only one of the two fits, the price wins — see composeTitleWithPrice.
      return composeTitleWithPrice(base, priceToken, ` - ${this.env.appName}`);
    };

    // Helper function to create title for the jobs page
    const constructJobTitle = (): string => {
      const firstChar = this.vertical?.cat?.charAt(0)?.toUpperCase() ?? '';
      const prefix = `${firstChar + this.vertical?.cat?.slice(1)} jobs`;
      const location = this.getRegionAndCityMetaTitle();
      return `${prefix}${location}${jobTitleSuffix}`;
    };

    // Helper function to create description for the jobs page
    const constructJobDesc = (): string => {
      const prefix = `${this.t.transform(
        'PAGES.VERTICAL.FIND_JOBS',
      )} ${this.vertical?.cat?.toLowerCase()} ${this.t.transform(
        'PAGES.VERTICAL.FIND_JOBS_2',
      )}`;
      const skills = this.vertical?.sPM?.toLowerCase();
      return `${prefix} ${skills}${this.cityOrRegionText}.`;
    };

    // Decide title and description based on the type of the page
    const regularTitle = this.isJobsPage ? null : constructRegularTitle();
    const titleTag = regularTitle
      ? this.ensureNoSpacesBeforePeriod(regularTitle.title)
      : constructJobTitle();
    const metaDesc = this.isJobsPage
      ? constructJobDesc()
      : this.ensureNoSpacesBeforePeriod(
          this.appendDescriptionPrice(
            `${this.vertical?.mT}${this.cityOrRegionText}.`,
          ),
        );

    try {
      // Construct fullUrl from vertical's category structure
      const slug = this.vertical?.newSlug?.trim() || this.vertical?.slug;
      let fullUrl = '';

      if (this.vertical) {
        if (
          this.vertical.mainCategory?.slug &&
          this.vertical.subCategory?.slug
        ) {
          fullUrl = `${this.vertical.mainCategory.slug}/${this.vertical.subCategory.slug}/${slug}`;
        } else if (this.vertical.mainCategory?.slug) {
          fullUrl = `${this.vertical.mainCategory.slug}/${slug}`;
        } else if (this.vertical.subCategory?.slug) {
          fullUrl = `${this.vertical.subCategory.slug}/${slug}`;
        } else {
          fullUrl = slug || '';
        }
      }

      // Remove duplicate consecutive parameters from fullUrl
      const cleanedFullUrl = this.removeDuplicateParams(`/${fullUrl}`);

      // Add appropriate suffix based on page type
      const suffix = this.isFindPage
        ? this.seg.suffix.serviceVertical
        : this.seg.section.jobs;

      // Build canonical URL with region and city if present
      let canonicalUrl = `${this.env.domain}${cleanedFullUrl}`;

      // Add region and city to canonical URL if they exist
      const citySlug = this.getCitySlug();
      if (citySlug && this.vertical?.region) {
        // City page includes region
        const citySuffix = this.isFindPage
          ? this.seg.suffix.city
          : this.seg.suffix.region;
        canonicalUrl = `${canonicalUrl}/${this.vertical.region.regionURL}/${citySlug}/${suffix}/${citySuffix}`;
      } else if (this.vertical?.region) {
        // Region-only page
        canonicalUrl = `${canonicalUrl}/${this.vertical?.region.regionURL}/${suffix}/${this.seg.suffix.region}`;
      } else {
        // No region or city
        canonicalUrl = `${canonicalUrl}/${suffix}`;
      }
      // SEO META
      this.seoService.setPageMeta({
        metaTitle: titleTag,
        metaDesc: metaDesc,
        img: this.vertical?.imgUrl,
        canonicalUrl: this.isJobsPage
          ? `${this.env.domain}${this.router.url}`
          : canonicalUrl,
        // False only when the brand suffix had to yield its characters to a
        // price token; jobs pages carry their own suffix and are untouched.
        appendSiteName: regularTitle?.appendSiteName ?? true,
      });

      // Manual noindex switch (per-vertical / per-geo). The API returns the
      // effective, page-level flag; the page still renders normally (HTTP 200).
      this.seoService.setRobotsNoindex(!!this.vertical?.noindex);
    } catch (err) {
      console.log(err);
    }
  }

  public setGetQuote(chosen: string) {
    this.getQuoteChosen = chosen;
  }

  public openAuthDialog() {
    this.currentParams = this.router.url;
    this.externalService.goToSignup();
  }

  public onPriceAndReviewRendered(rendered: boolean) {
    this.priceAndReviewRendered = rendered;
    this.setupDeepLinkButtons();
  }

  openHowItWorksDialog(): void {
    if (this.isHowItWorksDialogOpen) {
      return;
    }

    this.isHowItWorksDialogOpen = true;
    const dialogRef = this.dialog.open(HowItWorksDialogComponent, {
      disableClose: true,
      panelClass: 'full-screen-modal-mobile',
      width: '500px',
      maxWidth: '90vw',
      data: {},
    });

    dialogRef.afterClosed().subscribe(() => {
      this.isHowItWorksDialogOpen = false;
    });
  }

  setupDeepLinkButtons() {
    // Only setup buttons if we have the necessary data
    if (!this.vertical) {
      return;
    }

    let newButtons: DeepLinkButton[] = [
      {
        label: 'COMPONENTS.HOW_IT_WORKS_DIALOG.TITLE',
        action: () => this.openHowItWorksDialog(),
        show: true,
        icon: 'play-circle',
        noHighlight: true,
      },
      {
        label: 'COMPONENTS.DEEP_LINK_BUTTONS.MEET_SOME_HANDYHANDS',
        action: () => this.scrollToBestAssociatedUsers(),
        show: true,
      },
      {
        label: 'COMPONENTS.DEEP_LINK_BUTTONS.EXAMPLES',
        action: () => this.scrollToAssociatedTasks(),
        show: this.hasAssociatedTasks,
      },
      {
        label: 'COMPONENTS.DEEP_LINK_BUTTONS.FAQ',
        action: () => this.scrollToFaqContent(),
        show: this.hasFaqContent,
      },
      {
        label: 'COMPONENTS.DEEP_LINK_BUTTONS.PRICES_AND_REVIEWS',
        action: () => this.scrollToPriceAndReview(),
        show: this.priceAndReviewRendered,
      },
      {
        label: 'COMPONENTS.DEEP_LINK_BUTTONS.GOOD_TO_KNOW',
        action: () => this.scrollToGoodToKnow(),
        show: this.hasGoodToKnow,
      },
    ];

    // Only update if the buttons have actually changed
    if (JSON.stringify(this.deepLinkButtons) !== JSON.stringify(newButtons)) {
      this.deepLinkButtons = newButtons;
    }
  }

  scrollToBestAssociatedUsers() {
    if (this.isBrowser) {
      const bestAssociatedUsers = document.querySelector(
        'app-best-associated-users',
      );
      if (bestAssociatedUsers) {
        bestAssociatedUsers.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
    }
  }

  scrollToAssociatedTasks() {
    if (this.isBrowser) {
      const associatedTasks = document.querySelector('app-associated-tasks');
      if (associatedTasks) {
        associatedTasks.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
    }
  }

  scrollToFaqContent() {
    if (this.isBrowser) {
      const faqContent = document.querySelector('app-faq-content');
      if (faqContent) {
        faqContent.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
    }
  }

  scrollToPriceAndReview() {
    if (this.isBrowser) {
      // Try mini-vertical price review first, then fall back to regular price and review
      const priceAndReview =
        document.querySelector('app-mini-vertical-price-review') ||
        document.querySelector('app-price-and-review');
      if (priceAndReview) {
        priceAndReview.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
    }
  }

  scrollToGoodToKnow() {
    if (this.isBrowser) {
      const goodToKnow = document.querySelector('app-good-to-know');
      if (goodToKnow) {
        goodToKnow.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
    }
  }

  scrollToHowItWorks() {
    if (this.isBrowser) {
      const howItWorks = document.querySelector('app-how-it-works');
      if (howItWorks) {
        howItWorks.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
    }
  }

  scrollToPopularRoutes() {
    if (this.isBrowser) {
      const popularRoutes = document.querySelector('.popular-routes-section');
      if (popularRoutes) {
        popularRoutes.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
    }
  }

  /**
   * Fetch tasks associated to cat param
   * Use in associated-tasks component and for Google Schema
   */
  private fetchAssociatedTasks(tagId?: number) {
    if (!tagId) return;
    // SSR: skip this live fetch. It depends on the vertical response (tagId,
    // kW), so it can only start after fetchVertical resolves — a sequential
    // second round-trip that pushes slow renders past the 4s SSR budget.
    // The client re-runs this path after hydration (vertical comes from
    // TransferState) and fetches with the browser's 12s HTTP budget instead.
    if (!this.isBrowser) return;
    this.taskService
      .fetchAssociatedTasksByTagAndLocation({
        region: this.vertical?.region?.regionURL,
        city: this.vertical?.city?.slug,
        keywords: this.vertical?.kW,
        tagId,
      })
      .pipe(takeUntil(merge(this.destroy$, this.cancel$)))
      .subscribe({
        next: (res) => {
          this.setAssociatedTasks(this.convertLiveTaskAmountsToMajor(res));
          this.cdr.markForCheck();
        },
        error: (err) => {
          console.warn('Error fetching associated tasks:', err?.message);
          this.noRelTasks = true;
          this.hasAssociatedTasks = false;
          this.cdr.markForCheck();
        },
      });
  }

  /**
   * fetch-associated-tasks-by-tag-location returns budget/priceBid in minor
   * units, unlike vertical.similiarTasks/taskExamples which are already major
   * units, so only this live-fetch path needs conversion.
   */
  private convertLiveTaskAmountsToMajor(tasks: Task[]): Task[] {
    return (tasks ?? []).map((task) => ({
      ...task,
      budget:
        typeof task?.budget === 'number'
          ? minorToMajor(task.budget)
          : task?.budget,
      offers: task?.offers?.map((offer) => ({
        ...offer,
        priceBid:
          typeof offer?.priceBid === 'number'
            ? minorToMajor(offer.priceBid)
            : offer?.priceBid,
      })),
    }));
  }

  /**
   * Format response from API to relatedTasks Task[]
   */
  private setAssociatedTasks(res: Task[]) {
    if (res && res.length > 0) {
      this.relTasks = res;
      this.noRelTasks = false;
      this.hasAssociatedTasks = true;
      this.mapTaskSchema();
    } else {
      this.noRelTasks = true;
      this.hasAssociatedTasks = false;
    }
    this.priceReviewShowing = this.relTasks?.length > 0;
  }

  private normalDist(arr: number[]) {
    const usable = (arr ?? []).filter(
      (value) =>
        typeof value === 'number' && Number.isFinite(value) && value > 0,
    );
    // `relTasks` can hold two or more tasks that carry no usable bids between
    // them. `quantile` returns undefined for an empty array, which would write
    // NaN over a perfectly good displayPrice fallback.
    if (!usable.length) {
      return;
    }
    this.q25Price = Math.ceil(this.quantile(usable, 0.25) / 10) * 10;
    this.q50Price = Math.ceil(this.quantile(usable, 0.5) / 10) * 10;
    this.q75Price = Math.ceil(this.quantile(usable, 0.75) / 10) * 10;
    // Live bids supersede the fallback, so the flag must follow them.
    this.priceIsFallback = false;
  }

  /**
   * True once live offer bids have produced a usable price.
   */
  private hasLivePrice(): boolean {
    return Number.isFinite(this.q50Price) && this.q50Price > 0;
  }

  /**
   * Prices belong to a single vertical. Without this reset, `hasLivePrice()`
   * reads the previous vertical's percentiles during client-side navigation and
   * skips the new vertical's fallback — showing the wrong service's price.
   */
  private resetPriceState(): void {
    this.q25Price = 0;
    this.q50Price = 0;
    this.q75Price = 0;
    this.priceIsFallback = false;
  }

  /**
   * Populate the price percentiles from the vertical's stored `displayPrice`
   * when live offer data could not. Live offers always win — this only fills a
   * gap, and does nothing at all for verticals where the field is absent or
   * cannot be published (see `resolveDisplayPriceBand`).
   */
  private applyDisplayPriceFallback(): void {
    if (this.hasLivePrice()) {
      return;
    }
    const band = resolveDisplayPriceBand(this.vertical?.displayPrice);
    if (!band) {
      return;
    }
    this.q25Price = band.q25;
    this.q50Price = band.q50;
    this.q75Price = band.q75;
    this.priceIsFallback = true;
  }

  /**
   * Append the `displayPrice` sentence to the meta description when it fits
   * the 160-char cap. All confidence tiers may appear here (the tier decides
   * the form — typical / range / from-price), unlike the title token.
   */
  private appendDescriptionPrice(base: string): string {
    const sentence = formatDescriptionPriceSentence(
      this.vertical?.displayPrice,
      this.seoCountryConfig.priceToken,
    );
    if (!sentence) {
      return base;
    }
    const withPrice = `${base} ${sentence}`;
    return withPrice.length <= 160 ? withPrice : base;
  }

  private quantile = (arr: number[], q: number) => {
    const sorted = arr.sort((a, b) => a - b);
    const pos = (sorted.length - 1) * q;
    const base = Math.floor(pos);
    const rest = pos - base;
    if (sorted[base + 1] !== undefined) {
      return sorted[base] + rest * (sorted[base + 1] - sorted[base]);
    }
    return sorted[base];
  };

  private mapTaskSchema() {
    if (this.relTasks.length >= 2) {
      const offers = this.relTasks
        .filter((task) => task && task.offers && task.offers.length > 0)
        .flatMap((task) => task.offers.map((offer) => offer.priceBid));
      this.normalDist(offers);
    }
  }

  /**
   * JSONLDSchema for Google
   */
  private injectJSONLDSchema() {
    if (!this.vertical) {
      return;
    }

    this.seoService.setVerticalSchema(this.partsOfUrl ?? []);
    this.seoService.setEnhancedServiceSchema(this.vertical, this.router.url);
  }

  capitalizeFirstLetter(sentence: string) {
    if (!sentence) return '';
    return sentence.charAt(0).toUpperCase() + sentence.slice(1);
  }

  setBreadCrumb(v: Vertical): void {
    const breadcrumbSlug = this.isNetherland ? v.slug : v.newSlug || v.slug;
    this.partsOfUrl = setBreadCrumb({
      entity: v,
      hubIndex: this.seg.suffix.hubIndex,
      url: `${this.categoryPreFixHelper(v)}/${breadcrumbSlug}/${
        this.seg.suffix.serviceVertical
      }`,
      isVertical: true,
    });
    // Strip identical param matching from categories
    const currentParam =
      this.url?.toLowerCase() === v.slug
        ? `/${v.slug}`
        : `/${this.url}/${breadcrumbSlug}`;
    if (v?.region?.regionURL) {
      this.partsOfUrl.push({
        name: this.regionTransformations(
          this.capitalizeFirstLetter(v?.region?.name),
        ),
        url: `${currentParam}/${v?.region?.regionURL}/${this.seg.suffix.serviceVertical}/${this.seg.suffix.region}`,
      });
    }
    const cityName = this.getCityDisplayName();
    const citySlug = this.getCitySlug();
    if (this.routeCitySlug && cityName && citySlug && v.region?.regionURL) {
      this.partsOfUrl.push({
        name: cityName,
        url: `${currentParam}/${v.region?.regionURL}/${citySlug}/${this.seg.suffix.serviceVertical}/${this.seg.suffix.city}`,
      });
    }
  }

  setBreadCrumbJobPage(v: Vertical): void {
    const slug = this.isNetherland ? v.slug : v?.newSlug || v.slug;
    this.partsOfUrl = setBreadCrumb({
      entity: v,
      hubIndex: this.seg.suffix.hubIndex,
      url: `${this.seg.section.jobs}/${slug}${this.categoryPreFixHelper()}`,
    });
    if (v?.region?.regionURL) {
      this.partsOfUrl.push({
        name: v?.region?.name,
        url: `${this.url}${this.categoryPreFixHelper(v)}/${slug}/${
          v?.region?.regionURL
        }/${this.seg.suffix.region}`,
      });
    }
    const cityName = this.getCityDisplayName();
    const citySlug = this.getCitySlug();
    if (this.routeCitySlug && cityName && citySlug && v.region?.regionURL) {
      this.partsOfUrl.push({
        name: cityName,
        url: `${this.url}${this.categoryPreFixHelper(v)}/${slug}/${
          v.region?.regionURL
        }/${citySlug}/${this.seg.suffix.city}`,
      });
    }
    return;
  }

  public categoryPreFixHelper(v?: Vertical): string {
    const verticalSlug = v?.newSlug || v?.slug;
    if (this.vertical?.mainCategory?.slug && this.vertical?.subCategory?.slug) {
      // Remove identical param matching
      if (
        verticalSlug?.toLowerCase() === this.vertical.mainCategory.slug ||
        verticalSlug?.toLowerCase() === this.vertical.subCategory.slug
      ) {
        return `/${this.vertical.mainCategory.slug}`;
      }
      return `/${this.vertical.mainCategory.slug}/${this.vertical.subCategory.slug}`;
    } else if (this.vertical?.mainCategory && !this.vertical.subCategory) {
      // Remove identical param matching
      if (verticalSlug?.toLowerCase() === this.vertical.mainCategory.slug) {
        return '';
      }
      return `/${this.vertical.mainCategory.slug}`;
    } else if (this.vertical?.subCategory?.slug) {
      // Remove identical param matching
      if (verticalSlug?.toLowerCase() === this.vertical.subCategory.slug) {
        return '';
      }
      return `/${this.vertical.subCategory.slug}`;
    }
    return '';
  }
}
